import { IMPCSeriesLine } from "../models/options.models";
import getSeriesNameStrippingForecast from "./getSeriesNameStrippingForecast";

// ECharts' own colour for an unselected legend item.
const HIDDEN_LEGEND_COLOR = "#ccc";

/** The product a series draws: "Oil" for "Oil Historical" and every Oil forecast and backfit. */
export const getSeriesProduct = (name: string): string =>
  getSeriesNameStrippingForecast(name)?.split(" Historical")[0].trim();

export const toggleHiddenSeries = (
  hiddenSeries: string[] = [],
  name: string
): string[] => {
  const product = getSeriesProduct(name);
  return hiddenSeries.includes(product)
    ? hiddenSeries.filter((hidden) => hidden !== product)
    : [...hiddenSeries, product];
};

/**
 * Hides a product by drawing its series invisibly rather than removing them, so
 * every axis keeps the range it has with them showing. `silent` keeps them out
 * of hover, and so out of the tooltip.
 *
 * The legend keeps every item selected, as the series must stay in for the
 * axes, so a hidden item is greyed here the way ECharts greys an unselected one.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withHiddenSeries = (options: any, hiddenSeries: string[] = []) => {
  if (!options?.series) return options;

  const isHidden = (name: string) => hiddenSeries.includes(getSeriesProduct(name));

  const series = options.series.map((trace: IMPCSeriesLine) => {
    const hidden = isHidden(trace.name);
    if (!hidden && !trace.isHidden) return trace;

    return {
      ...trace,
      isHidden: hidden,
      silent: hidden,
      lineStyle: hidden
        ? { ...trace.lineStyle, opacity: 0 }
        : {
            ...trace.lineStyle,
            color: trace.lineStyle.originalColor,
            width: trace.lineStyle.originalWidth,
            opacity: 1
          },
      itemStyle: { ...trace.itemStyle, opacity: hidden ? 0 : 1 }
    };
  });

  const legendData = options.legend?.data?.map((item) => {
    const name = typeof item === "string" ? item : item.name;
    return isHidden(name)
      ? {
          name,
          textStyle: { color: HIDDEN_LEGEND_COLOR },
          itemStyle: {
            color: HIDDEN_LEGEND_COLOR,
            borderColor: HIDDEN_LEGEND_COLOR,
            opacity: 1
          },
          lineStyle: { color: HIDDEN_LEGEND_COLOR, opacity: 1 }
        }
      : name;
  });

  return {
    ...options,
    series,
    ...(legendData && { legend: { ...options.legend, data: legendData } })
  };
};
